import { useEffect } from 'react';
import { DimensionType } from '@sleed/bizcommerce-api';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useDimensionMutations, useDimensionTypes } from '~/hooks/use-dimensions';
import { dimensionSchema, EMPTY_DIMENSION, type DimensionFormValues } from '~/lib/schemas/dimension';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: DimensionFormValues | null;
}

/** Add / edit a dimension (Διάσταση). */
export function DimensionEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useDimensionMutations();
	const { data: types } = useDimensionTypes();
	const form = useForm<DimensionFormValues>({
		resolver: zodResolver(dimensionSchema) as Resolver<DimensionFormValues>,
		defaultValues: initial ?? EMPTY_DIMENSION,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, control, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_DIMENSION);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('attributes.dimensions.editTitle') : t('attributes.dimensions.addTitle')}</DialogTitle>
					<DialogDescription>{t('attributes.dimensions.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('attributes.dimensions.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('attributes.dimensions.columns.type')} error={formState.errors.dt?.message}>
						<Controller
							control={control}
							name="dt"
							render={({ field }) => (
								<Select value={field.value != null ? String(field.value) : ''} onValueChange={v => field.onChange(Number(v))}>
									<SelectTrigger aria-invalid={Boolean(formState.errors.dt)}>
										<SelectValue placeholder={t('attributes.dimensions.selectType')} />
									</SelectTrigger>
									<SelectContent>
										{(types ?? []).map(ty => (
											<SelectItem key={ty.id} value={String(ty.id)}>
												{ty.nm}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
					</FormField>
					<FormField label={t('attributes.dimensions.columns.erpCode')} error={formState.errors.erpcd?.message}>
						<Input {...register('erpcd')} maxLength={50} aria-invalid={Boolean(formState.errors.erpcd)} />
					</FormField>
					{watch('dt') === DimensionType.Custom && (
						<div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
							<div className="flex flex-col">
								<Label htmlFor="dim-flt">{t('attributes.dimensions.columns.filterable')}</Label>
								<span className="text-xs text-foreground-muted">{t('attributes.dimensions.filterableHint')}</span>
							</div>
							<Switch id="dim-flt" checked={watch('flt')} onCheckedChange={next => setValue('flt', next, { shouldDirty: true })} />
						</div>
					)}
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="dim-iv">{t('attributes.dimensions.columns.visible')}</Label>
							<Switch id="dim-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<FormField label={t('attributes.dimensions.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
